// =============================================================================
// All Etsy Payments countries — verified against Etsy's official policies
// (Apr 2026 revision).
//
// CONFIDENCE LEVELS:
//   verified: true   — rate confirmed via Etsy help center or multiple sources
//   verified: false  — best-estimate based on regional patterns (EU = 4% + €0.30,
//                      Payoneer = 6.5% + $0.30 USD); marked in the UI
//
// REGULATORY FEES (current, before Jun 22, 2026):
//   Türkiye 2.27% · Vietnam 1.24% · Canada 1.15% · Spain 0.72%
//   France 0.47% · UK 0.32% · Italy 0.32% · India 0.29%
//
// JUNE 22, 2026 CHANGES — see `regulatoryAfterJun22_2026` field where it
//     differs. After Jun 22, swap that value into `regulatory`.
//
// Sources:
//   https://help.etsy.com/hc/en-us/articles/115015710408 (country list)
//   https://help.etsy.com/hc/en-us/articles/1500011073202 (regulatory fees)
//   https://www.etsy.com/legal/etsy-payments/ (Payoneer countries)
// =============================================================================

const eu = (overrides = {}) => ({
  symbol: '€', code: 'EUR',
  processing: { pct: 0.040, fixed: 0.30 },
  regulatory: 0,
  region: 'EU',
  verified: false,
  ...overrides,
});

const payoneer = (overrides = {}) => ({
  symbol: '$', code: 'USD',
  processing: { pct: 0.065, fixed: 0.30 },
  regulatory: 0,
  region: 'Payoneer',
  payoneer: true,
  verified: false,
  ...overrides,
});

export const countries = {
  // ─── North America ───────────────────────────────────────────
  US: { name: 'United States',          flag: '🇺🇸', symbol: '$',    code: 'USD',
        processing: { pct: 0.030, fixed: 0.25 }, regulatory: 0,
        region: 'Americas', verified: true },
  CA: { name: 'Canada',                 flag: '🇨🇦', symbol: 'CA$',  code: 'CAD',
        processing: { pct: 0.030, fixed: 0.25 }, regulatory: 0.0115,
        region: 'Americas', verified: true },
  MX: { name: 'Mexico',                 flag: '🇲🇽', symbol: 'MX$',  code: 'MXN',
        processing: { pct: 0.040, fixed: 5.50 }, regulatory: 0,
        region: 'Americas', verified: false },

  // ─── United Kingdom & Eurozone ───────────────────────────────
  UK: { name: 'United Kingdom',         flag: '🇬🇧', symbol: '£',    code: 'GBP',
        processing: { pct: 0.040, fixed: 0.20 }, regulatory: 0.0032,
        regulatoryAfterJun22_2026: 0.0048,
        region: 'Europe', verified: true },
  IE: eu({ name: 'Ireland',             flag: '🇮🇪' }),
  DE: eu({ name: 'Germany',             flag: '🇩🇪' }),
  FR: eu({ name: 'France',              flag: '🇫🇷', regulatory: 0.0047,
           regulatoryAfterJun22_2026: 0.0114, verified: true }),
  IT: eu({ name: 'Italy',               flag: '🇮🇹', regulatory: 0.0032,
           regulatoryAfterJun22_2026: 0.0080, verified: true }),
  ES: eu({ name: 'Spain',               flag: '🇪🇸', regulatory: 0.0072,
           regulatoryAfterJun22_2026: 0.0088, verified: true }),
  NL: eu({ name: 'Netherlands',         flag: '🇳🇱' }),
  BE: eu({ name: 'Belgium',             flag: '🇧🇪' }),
  AT: eu({ name: 'Austria',             flag: '🇦🇹' }),
  PT: eu({ name: 'Portugal',            flag: '🇵🇹' }),
  GR: eu({ name: 'Greece',              flag: '🇬🇷' }),
  FI: eu({ name: 'Finland',             flag: '🇫🇮' }),
  LU: eu({ name: 'Luxembourg',          flag: '🇱🇺' }),
  CY: eu({ name: 'Cyprus',              flag: '🇨🇾' }),
  MT: eu({ name: 'Malta',               flag: '🇲🇹' }),
  EE: eu({ name: 'Estonia',             flag: '🇪🇪' }),
  LV: eu({ name: 'Latvia',              flag: '🇱🇻' }),
  LT: eu({ name: 'Lithuania',           flag: '🇱🇹' }),
  SK: eu({ name: 'Slovakia',            flag: '🇸🇰' }),
  SI: eu({ name: 'Slovenia',            flag: '🇸🇮' }),
  HR: eu({ name: 'Croatia',             flag: '🇭🇷' }),

  // ─── Non-Eurozone EU ─────────────────────────────────────────
  PL: { name: 'Poland',                 flag: '🇵🇱', symbol: 'zł',   code: 'PLN',
        processing: { pct: 0.040, fixed: 1.30 }, regulatory: 0,
        region: 'Europe', verified: false },
  CZ: { name: 'Czech Republic',         flag: '🇨🇿', symbol: 'Kč',   code: 'CZK',
        processing: { pct: 0.040, fixed: 7.00 }, regulatory: 0,
        region: 'Europe', verified: false },
  HU: { name: 'Hungary',                flag: '🇭🇺', symbol: 'Ft',   code: 'HUF',
        processing: { pct: 0.040, fixed: 110 }, regulatory: 0,
        regulatoryAfterJun22_2026: 0.0197,
        region: 'Europe', verified: false },
  RO: { name: 'Romania',                flag: '🇷🇴', symbol: 'lei',  code: 'RON',
        processing: { pct: 0.040, fixed: 1.50 }, regulatory: 0,
        region: 'Europe', verified: false },
  BG: { name: 'Bulgaria',               flag: '🇧🇬', symbol: 'лв',   code: 'BGN',
        processing: { pct: 0.040, fixed: 0.60 }, regulatory: 0,
        region: 'Europe', verified: false },
  SE: { name: 'Sweden',                 flag: '🇸🇪', symbol: 'kr',   code: 'SEK',
        processing: { pct: 0.040, fixed: 3.00 }, regulatory: 0,
        region: 'Europe', verified: false },
  DK: { name: 'Denmark',                flag: '🇩🇰', symbol: 'kr',   code: 'DKK',
        processing: { pct: 0.040, fixed: 2.50 }, regulatory: 0,
        region: 'Europe', verified: false },

  // ─── Other Europe ────────────────────────────────────────────
  NO: { name: 'Norway',                 flag: '🇳🇴', symbol: 'kr',   code: 'NOK',
        processing: { pct: 0.040, fixed: 3.00 }, regulatory: 0,
        region: 'Europe', verified: false },
  CH: { name: 'Switzerland',            flag: '🇨🇭', symbol: 'CHF',  code: 'CHF',
        processing: { pct: 0.040, fixed: 0.30 }, regulatory: 0,
        region: 'Europe', verified: false },

  // ─── Oceania ─────────────────────────────────────────────────
  AU: { name: 'Australia',              flag: '🇦🇺', symbol: 'AU$',  code: 'AUD',
        processing: { pct: 0.030, fixed: 0.25 }, regulatory: 0,
        region: 'Oceania', verified: true },
  NZ: { name: 'New Zealand',            flag: '🇳🇿', symbol: 'NZ$',  code: 'NZD',
        processing: { pct: 0.030, fixed: 0.25 }, regulatory: 0,
        region: 'Oceania', verified: false },

  // ─── Asia (Etsy Payments direct) ─────────────────────────────
  HK: { name: 'Hong Kong',              flag: '🇭🇰', symbol: 'HK$',  code: 'HKD',
        processing: { pct: 0.040, fixed: 2.30 }, regulatory: 0,
        region: 'Asia', verified: false },
  SG: { name: 'Singapore',              flag: '🇸🇬', symbol: 'S$',   code: 'SGD',
        processing: { pct: 0.040, fixed: 0.40 }, regulatory: 0,
        region: 'Asia', verified: false },
  MY: { name: 'Malaysia',               flag: '🇲🇾', symbol: 'RM',   code: 'MYR',
        processing: { pct: 0.040, fixed: 1.20 }, regulatory: 0,
        region: 'Asia', verified: false },
  ID: { name: 'Indonesia',              flag: '🇮🇩', symbol: 'Rp',   code: 'IDR',
        processing: { pct: 0.040, fixed: 4500 }, regulatory: 0,
        region: 'Asia', verified: false },
  PH: { name: 'Philippines',            flag: '🇵🇭', symbol: '₱',    code: 'PHP',
        processing: { pct: 0.040, fixed: 14.00 }, regulatory: 0,
        region: 'Asia', verified: false },
  VN: { name: 'Vietnam',                flag: '🇻🇳', symbol: '₫',    code: 'VND',
        processing: { pct: 0.040, fixed: 7000 }, regulatory: 0.0124,
        region: 'Asia', verified: false },

  // ─── Middle East / Africa ────────────────────────────────────
  IL: { name: 'Israel',                 flag: '🇮🇱', symbol: '₪',    code: 'ILS',
        processing: { pct: 0.040, fixed: 1.00 }, regulatory: 0,
        region: 'MEA', verified: false },
  TR: { name: 'Türkiye',                flag: '🇹🇷', symbol: '₺',    code: 'TRY',
        processing: { pct: 0.040, fixed: 1.00 }, regulatory: 0.0227,
        regulatoryAfterJun22_2026: 0.0167,
        region: 'MEA', verified: false },
  ZA: { name: 'South Africa',           flag: '🇿🇦', symbol: 'R',    code: 'ZAR',
        processing: { pct: 0.040, fixed: 5.00 }, regulatory: 0,
        region: 'MEA', verified: false },
  MA: { name: 'Morocco',                flag: '🇲🇦', symbol: 'DH',   code: 'MAD',
        processing: { pct: 0.040, fixed: 3.00 }, regulatory: 0,
        region: 'MEA', verified: false },

  // ─── Payoneer-based countries (USD-denominated) ──────────────
  AR: payoneer({ name: 'Argentina',     flag: '🇦🇷' }),
  BR: payoneer({ name: 'Brazil',        flag: '🇧🇷' }),
  CL: payoneer({ name: 'Chile',         flag: '🇨🇱' }),
  PE: payoneer({ name: 'Peru',          flag: '🇵🇪' }),
  CN: payoneer({ name: 'China',         flag: '🇨🇳' }),
  JP: payoneer({ name: 'Japan',         flag: '🇯🇵' }),
  KR: payoneer({ name: 'South Korea',   flag: '🇰🇷' }),
  TH: payoneer({ name: 'Thailand',      flag: '🇹🇭' }),
  IN: payoneer({ name: 'India',         flag: '🇮🇳', regulatory: 0.0029,
                 regulatoryAfterJun22_2026: 0.0005 }),
  PK: payoneer({ name: 'Pakistan',      flag: '🇵🇰' }),
  AE: payoneer({ name: 'UAE',           flag: '🇦🇪' }),
  EG: payoneer({ name: 'Egypt',         flag: '🇪🇬' }),
  GE: payoneer({ name: 'Georgia',       flag: '🇬🇪' }),
  KZ: payoneer({ name: 'Kazakhstan',    flag: '🇰🇿' }),
  RS: payoneer({ name: 'Serbia',        flag: '🇷🇸' }),
  UA: payoneer({ name: 'Ukraine',       flag: '🇺🇦' }),
};

// Region order for the dropdown (most common first)
export const REGION_ORDER = ['Americas', 'Europe', 'EU', 'Oceania', 'Asia', 'MEA', 'Payoneer'];
export const REGION_LABELS = {
  Americas: '🌎 Americas',
  Europe:   '🌍 Europe (non-Eurozone)',
  EU:       '🇪🇺 Eurozone (€)',
  Oceania:  '🌏 Oceania',
  Asia:     '🌏 Asia',
  MEA:      '🌍 Middle East / Africa',
  Payoneer: '💱 Payoneer (USD)',
};

// Offsite Ads
export const OFFSITE_RATES = { under10k: 0.15, over10k: 0.12 };
export const OFFSITE_CAP = 100;
